import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Flex,
  Image,
  VStack,
  HStack,
  Icon,
  useColorModeValue,
  Badge,
  Divider,
  Avatar,
} from '@chakra-ui/react';
import { FaQuoteLeft, FaBriefcase, FaUsers, FaLightbulb, FaAward, FaRocket } from 'react-icons/fa';

const About: React.FC = () => {
  const bgGradient = useColorModeValue(
    'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(13,211,205,0.05) 100%)',
    'linear-gradient(180deg, rgba(26,32,44,0) 0%, rgba(13,211,205,0.1) 100%)'
  );

  return (
    <Box py={20} pt="160px" position="relative" overflow="hidden" bgGradient={bgGradient}>
      <Container maxW="1200px" position="relative" zIndex={1}>
        <Box textAlign="center" mb={16}>
          <Badge
            colorScheme="teal"
            mb={4}
            px={3}
            py={1}
            borderRadius="full"
            fontWeight="bold"
          >
            了解我们的故事
          </Badge>
          <Heading
            as="h1"
            size={{ base: "2xl", md: "3xl" }}
            fontWeight="bold"
            mb={6}
            bgGradient="linear(to-r, brand.cyan, teal.400)"
            bgClip="text"
          >
            关于我们
          </Heading>
          <Text
            fontSize={{ base: "lg", md: "xl" }}
            color="gray.600"
            maxW="800px"
            mx="auto"
            mb={12}
          >
            Y-Square AI 致力于通过创新的人工智能技术赋能企业数字化转型
          </Text>
        </Box>

        {/* 公司简介 */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10} mb={20} alignItems="center">
          <Box>
            <Heading as="h2" size="xl" mb={6} color="brand.cyan">
              我们的故事
            </Heading>
            <Text fontSize="lg" color="gray.600" mb={4} lineHeight="tall">
              元垚科技成立于2024年，由一群来自顶尖科技公司和研究机构的AI专家共同创立。我们的使命是通过先进的人工智能技术，帮助企业创造价值、提升效率、实现创新。
            </Text>
            <Text fontSize="lg" color="gray.600" mb={4} lineHeight="tall">
              经过几年的发展，我们已经成为中国领先的AI解决方案提供商，拥有自主研发的核心技术和丰富的行业经验，为金融、制造、零售、医疗等多个行业的数百家企业提供了专业的AI产品和服务。
            </Text>
            <Text fontSize="lg" color="gray.600" lineHeight="tall">
              我们相信，人工智能将重塑未来商业和社会的运作方式，元垚科技将持续创新，与合作伙伴一起开创智能化的未来。
            </Text>
          </Box>
          <Box position="relative">
            <Box
              position="absolute"
              top="-20px"
              left="-20px"
              bg="brand.purple"
              width="200px"
              height="200px"
              opacity="0.1"
              borderRadius="lg"
              zIndex={-1}
            />
            <Box
              position="absolute"
              bottom="-20px"
              right="-20px"
              bg="brand.cyan"
              width="200px"
              height="200px"
              opacity="0.1"
              borderRadius="lg"
              zIndex={-1}
            />
            <Image
              src="https://images.unsplash.com/photo-1568992688065-536aad8a12f6?ixlib=rb-4.0.3&auto=format&fit=crop"
              alt="公司照片"
              borderRadius="lg"
              boxShadow="xl"
              width="100%"
            />
          </Box>
        </SimpleGrid>

        {/* 我们的愿景和使命 */}
        <Box
          bg="white"
          borderRadius="xl"
          boxShadow="xl"
          p={{ base: 8, md: 12 }}
          mb={20}
          position="relative"
          overflow="hidden"
        >
          <Box
            position="absolute"
            top="-50px"
            right="-50px"
            bg="brand.cyan"
            opacity="0.05"
            width="200px"
            height="200px"
            borderRadius="full"
          />

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10}>
            <VStack align="start" spacing={6}>
              <Flex
                width="60px"
                height="60px"
                borderRadius="lg"
                bg="brand.cyan"
                align="center"
                justify="center"
                color="white"
              >
                <Icon as={FaLightbulb} boxSize={6} />
              </Flex>
              <Heading as="h3" size="lg" color="brand.cyan">
                我们的使命
              </Heading>
              <Text fontSize="lg" color="gray.600" lineHeight="tall">
                通过先进的人工智能技术赋能企业，帮助他们提升效率、创造价值、实现创新，推动AI技术的普及与应用。
              </Text>
            </VStack>

            <VStack align="start" spacing={6}>
              <Flex
                width="60px"
                height="60px"
                borderRadius="lg"
                bg="brand.purple"
                align="center"
                justify="center"
                color="white"
              >
                <Icon as={FaRocket} boxSize={6} />
              </Flex>
              <Heading as="h3" size="lg" color="brand.purple">
                我们的愿景
              </Heading>
              <Text fontSize="lg" color="gray.600" lineHeight="tall">
                成为全球领先的AI技术创新企业，让人工智能成为推动社会进步和行业发展的重要力量，共同创造更美好的未来。
              </Text>
            </VStack>
          </SimpleGrid>
        </Box>

        {/* 核心价值观 */}
        <Box mb={20}>
          <Heading
            as="h2"
            size="xl"
            textAlign="center"
            mb={12}
            position="relative"
            _after={{
              content: '""',
              position: 'absolute',
              bottom: '-16px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '60px',
              height: '4px',
              bg: 'brand.purple',
              borderRadius: 'full',
            }}
          >
            核心价值观
          </Heading>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8}>
            {values.map((value, index) => (
              <VStack
                key={index}
                p={8}
                bg="white"
                borderRadius="xl"
                boxShadow="md"
                spacing={4}
                align="start"
                _hover={{
                  transform: "translateY(-5px)",
                  boxShadow: "xl",
                }}
                transition="all 0.3s ease"
                borderTop="4px solid"
                borderTopColor={value.color}
              >
                <Flex
                  width="50px"
                  height="50px"
                  borderRadius="lg"
                  bg={value.color}
                  align="center"
                  justify="center"
                  color="white"
                >
                  <Icon as={value.icon} boxSize={5} />
                </Flex>
                <Heading as="h3" size="md" color={value.titleColor}>
                  {value.title}
                </Heading>
                <Text color="gray.600" lineHeight="tall">
                  {value.description}
                </Text>
              </VStack>
            ))}
          </SimpleGrid>
        </Box>

      </Container>
    </Box>
  );
};

const values = [
  {
    title: '创新驱动',
    description: '我们不断探索AI技术前沿，积极推动技术创新，为客户提供最先进的解决方案。',
    icon: FaLightbulb,
    color: 'brand.purple',
    titleColor: 'brand.purple'
  },
  {
    title: '专业专注',
    description: '专注于AI领域的技术研发与应用，以专业的态度和深厚的技术积累服务客户。',
    icon: FaBriefcase,
    color: 'brand.blue',
    titleColor: 'brand.blue'
  },
  {
    title: '协作共赢',
    description: '与客户、合作伙伴建立长期合作关系，共同创造价值，实现多方共赢。',
    icon: FaUsers,
    color: 'brand.cyan',
    titleColor: 'brand.cyan'
  },
  {
    title: '务实高效',
    description: '注重实际业务价值，以结果为导向，高效解决客户问题，创造可衡量的商业价值。',
    icon: FaAward,
    color: 'teal.500',
    titleColor: 'teal.500'
  },
];

export default About;